import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ClientWeight } from '../types';

interface WeightChartProps {
  data: ClientWeight[];
}

const WeightChart: React.FC<WeightChartProps> = ({ data }) => {
  if (data.length === 0) return null;

  // Сортируем данные по дате
  const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Форматируем данные для Recharts
  const chartData = sortedData.map(item => ({
    date: format(new Date(item.date), 'dd.MM.yy', { locale: ru }),
    weight: item.weight,
    originalDate: item.date // для сортировки
  }));

  // Находим мин и макс для подсказок
  const minWeight = Math.min(...sortedData.map(d => d.weight));
  const maxWeight = Math.max(...sortedData.map(d => d.weight));
  const weightChange = sortedData.length > 1 
    ? sortedData[sortedData.length - 1].weight - sortedData[0].weight 
    : 0;

  const formatWeight = (value: number) => `${value.toFixed(1)} кг`;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-blue-600">
            Вес: <span className="font-bold">{formatWeight(payload[0].value)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">График прогресса веса</h3>
        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
          <span>Записей: {data.length}</span>
          <span>Мин: {formatWeight(minWeight)}</span>
          <span>Макс: {formatWeight(maxWeight)}</span>
          {weightChange !== 0 && (
            <span className={`font-medium ${weightChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
              Изменение: {weightChange > 0 ? '+' : ''}{formatWeight(weightChange)}
            </span>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            angle={-45}
            textAnchor="end"
            height={40}
            tick={{ fontSize: 11 }}
            stroke="#6b7280"
          />
          <YAxis 
            tickFormatter={formatWeight}
            tick={{ fontSize: 11 }}
            stroke="#6b7280"
            domain={['dataMin - 1', 'dataMax + 1']}
            width={35}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line 
            type="monotone" 
            dataKey="weight" 
            stroke="#3b82f6" 
            strokeWidth={3}
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#ffffff', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default WeightChart; 